/**
 * V3 — PWA Installer + Service Worker registration
 *
 * Affiche un bouton "📲 Installer l'app" en bas-droite quand le navigateur
 * supporte l'installation (Android/Chrome/Edge) ET que l'app n'est pas déjà installée.
 *
 * Sur iOS Safari, montre un mini-guide "Ajouter à l'écran d'accueil" (pas d'API auto).
 *
 * Le SW est enregistré au mount du composant.
 */
import React, { useEffect, useState } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

function isIOSSafari() {
  const ua = navigator.userAgent || '';
  const isiOS = /iPhone|iPad|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isiOS && isSafari;
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
}

export default function PWAInstaller() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());

  // Register service worker
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    Promise.resolve().then(() => {
      navigator.serviceWorker.register('/sw.js').catch(() => { /* ignore SW registration failure */ });
    });
  }, []);

  // Listen to install prompt
  useEffect(() => {
    if (installed) return;
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Only auto-show after user has been on page > 5s
      setTimeout(() => {
        // Don't re-show if user dismissed in last 7 days
        try {
          const dismissed = localStorage.getItem('gr_pwa_dismissed_at');
          if (dismissed && Date.now() - parseInt(dismissed, 10) < 7 * 24 * 3600_000) return;
        } catch (_e) { /* ignore */ }
        setShowBanner(true);
      }, 5000);
    };
    const onInstalled = () => { setInstalled(true); setShowBanner(false); setDeferredPrompt(null); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [installed]);

  // iOS auto-banner if not installed
  useEffect(() => {
    if (installed) return;
    if (!isIOSSafari()) return;
    try {
      const dismissed = localStorage.getItem('gr_pwa_dismissed_at');
      if (dismissed && Date.now() - parseInt(dismissed, 10) < 7 * 24 * 3600_000) return;
    } catch (_e) { /* ignore */ }
    const t = setTimeout(() => setShowBanner(true), 6000);
    return () => clearTimeout(t);
  }, [installed]);

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        setShowBanner(false);
        setDeferredPrompt(null);
      }
    } else if (isIOSSafari()) {
      setShowIOSGuide(true);
    }
  };

  const dismiss = () => {
    setShowBanner(false);
    setShowIOSGuide(false);
    try { localStorage.setItem('gr_pwa_dismissed_at', String(Date.now())); } catch (_e) { /* ignore */ }
  };

  if (installed || (!showBanner && !showIOSGuide)) return null;

  return (
    <>
      {showBanner && !showIOSGuide && (
        <div
          data-testid="pwa-install-banner"
          style={{
            position: 'fixed', bottom: 18, right: 18, zIndex: 100000,
            background: '#0f172a', border: '1px solid #fbbf24', borderRadius: 12,
            padding: 14, maxWidth: 320, boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            fontFamily: 'system-ui, sans-serif',
          }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <Smartphone size={28} color="#fbbf24" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#fbbf24', fontSize: 14 }}>Installer Groupe ROPS</div>
              <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 4, lineHeight: 1.4 }}>
                Ajoute l&apos;app sur ton écran d&apos;accueil pour un accès rapide, plein écran et hors-ligne.
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button
                  onClick={install}
                  data-testid="pwa-install-btn"
                  style={{
                    background: '#fbbf24', color: '#0f172a', border: 'none',
                    padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
                    fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5,
                  }}>
                  <Download size={13} /> Installer
                </button>
                <button
                  onClick={dismiss}
                  data-testid="pwa-dismiss-btn"
                  style={{
                    background: 'transparent', color: '#94a3b8', border: '1px solid #1e293b',
                    padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                  }}>
                  Plus tard
                </button>
              </div>
            </div>
            <button
              onClick={dismiss}
              aria-label="Fermer"
              style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0 }}
            ><X size={16} /></button>
          </div>
        </div>
      )}

      {showIOSGuide && (
        <div
          data-testid="pwa-ios-guide"
          onClick={dismiss}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.8)', zIndex: 100001,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
            fontFamily: 'system-ui, sans-serif',
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#0f172a', border: '1px solid #fbbf24', borderRadius: 12,
              padding: 24, maxWidth: 360, color: '#e2e8f0',
            }}>
            <div style={{ fontWeight: 700, color: '#fbbf24', fontSize: 16, marginBottom: 12 }}>
              📲 Installer sur iPhone / iPad
            </div>
            <ol style={{ fontSize: 13, lineHeight: 1.7, paddingLeft: 18, margin: 0, color: '#cbd5e1' }}>
              <li>Touche l&apos;icône <strong style={{ color: '#fbbf24' }}>Partager</strong> en bas de Safari</li>
              <li>Sélectionne <strong style={{ color: '#fbbf24' }}>&quot;Sur l&apos;écran d&apos;accueil&quot;</strong></li>
              <li>Touche <strong style={{ color: '#fbbf24' }}>&quot;Ajouter&quot;</strong> en haut à droite</li>
            </ol>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                onClick={dismiss}
                style={{
                  background: '#fbbf24', color: '#0f172a', border: 'none',
                  padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                }}>OK</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
